/**
 * AppShell — chrome for non-game routes.
 *
 * TG Phase 1 audit (do NOT duplicate):
 * - tg.ready()  called in main.tsx:62
 * - tg.expand() called in main.tsx:68
 * - themeParams applied in main.tsx:96 (applyTelegramTheme on boot) and
 *   subscribed via main.tsx:127-129 (onEvent themeChanged).
 *   useTelegramReady.ts:21 also calls tg.ready() (idempotent try/catch).
 *   useTelegramTheme.ts:51-70 provides the React hook variant (themeChanged
 *   subscription) for components that need reactive re-render — NOT used here.
 *
 * AppShell adds:
 *   - BackButton LIFO stack via useBackButton (default: navigate(-1))
 *   - safe-area padding via --safe-area-inset-* CSS variables
 *   - TopBar component above the page outlet
 *   - <Outlet /> for nested routes
 *
 * No TG WebApp re-initialization here.
 */

import { type ReactElement } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { TopBar } from './TopBar'
import { useBackButton } from '../hooks/useBackButton'
import { useSettingsDrawer } from './useSettingsDrawer'
import { useProfileDrawer } from './useProfileDrawer'
import { OfflineBanner } from './OfflineBanner'

export default function AppShell(): ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const settings = useSettingsDrawer()
  const profile = useProfileDrawer()

  // Default BackButton handler for all non-game routes.
  // Child routes that need custom navigation (e.g. navigate('/') instead of
  // navigate(-1)) call useBackButton themselves — their handler stacks on top
  // and wins while the route is mounted.
  useBackButton(() => {
    navigate(-1)
  }, [navigate])

  const isLobby = location.pathname === '/'

  return (
    <div className="app-shell">
      <TopBar
        lobby={isLobby}
        // On Lobby: avatar opens ProfileDrawer. Elsewhere: avatar navigates home.
        onAvatar={isLobby ? () => profile.open() : () => navigate('/')}
        onCog={() => settings.open()}
      />
      <OfflineBanner />
      <main className="app-shell__content">
        <Outlet />
      </main>
    </div>
  )
}
